"use client";

import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pagination, PaginationContent, PaginationItem } from "@/components/ui/pagination";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const accentBorders = ["border-t-[12px] border-t-[hsl(0_84%_60%)]", "border-t-[12px] border-t-[hsl(222_20%_12%)]", "border-t-[12px] border-t-[hsl(215_20%_40%)]"];
function stripHtml(html: string): string {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
}
function formatDate(dateStr?: string): string {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    }).toUpperCase();
  } catch {
    return "";
  }
}
function getThumbnail(post: any): string | null {
  if (!post) return null;
  if (post.featuredImage) return post.featuredImage;
  if (post.customFields?.featured_image) return post.customFields.featured_image;
  const htmlSources = [post.content, post.excerpt];
  for (const html of htmlSources) {
    if (typeof html === "string" && html.includes("<img")) {
      const match = html.match(/<img[^>]+src=["']([^"']+)["']/i);
      if (match && match[1]) {
        return match[1];
      }
    }
  }
  return null;
}
function ArchiveConsumer({
  searchTerm,
  category,
  sortOrder
}: {
  searchTerm: string;
  category: string;
  sortOrder: string;
}) {
  const {
    posts,
    loading,
    error,
    refetch,
    currentPage,
    totalPages,
    setPage,
    hasNext,
    hasPrev,
    nextPage,
    prevPage
  } = usePosts() as any;
  const allCategories = Array.from(new Map((posts || []).flatMap((p: any) => p.categories || []).map((c: any) => [c.slug, c])).values());
  let filteredPosts = (posts || []).slice();
  if (searchTerm.trim()) {
    const term = searchTerm.toLowerCase();
    filteredPosts = filteredPosts.filter((p: any) => (p.title || "").toLowerCase().includes(term) || stripHtml(p.excerpt || "").toLowerCase().includes(term));
  }
  if (category !== "all") {
    filteredPosts = filteredPosts.filter((p: any) => (p.categories || []).some((c: any) => c.slug === category));
  }
  filteredPosts.sort((a: any, b: any) => {
    const ad = a.date ? new Date(a.date).getTime() : 0;
    const bd = b.date ? new Date(b.date).getTime() : 0;
    return sortOrder === "oldest" ? ad - bd : bd - ad;
  });
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[...Array(6)].map((_, i) => <div data-index={i} key={i} className="h-[460px] bg-card border-[3px] border-border animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="border-[3px] border-border bg-card p-8 text-center">
        <p className="font-serif text-base text-foreground mb-4">
          Failed to load articles.
        </p>
        <Button onClick={refetch} className="bg-primary text-primary-foreground border-[3px] border-border rounded-md px-6 py-3 font-default uppercase tracking-wider hover:bg-primary/90">
          Retry
        </Button>
      </div>;
  }
  if (!filteredPosts || filteredPosts.length === 0) {
    return <div className="border-[3px] border-border bg-card p-12 text-center">
        <p className="font-mono text-sm uppercase tracking-[0.18em] text-muted-foreground">
          § NO ARTICLES FOUND
        </p>
      </div>;
  }
  return <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredPosts.map((post: any, i: number) => {
        const accent = accentBorders[i % accentBorders.length];
        const primaryCategory = post.categories && post.categories.length > 0 ? post.categories[0] : null;
        const excerpt = stripHtml(post.excerpt || "").slice(0, 180);
        const thumb = getThumbnail(post);
        return <article data-index={i} key={post.id} className={`group bg-card ${accent} border-x-[3px] border-b-[3px] border-border flex flex-col transition-all duration-200 hover:shadow-lg`}>
              <div className="relative aspect-[16/10] overflow-hidden border-b-[3px] border-border bg-muted">
                {thumb ? <img src={thumb} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover" /> : <div className="w-full h-full" style={{
              background: "repeating-radial-gradient(circle, var(--border) 0 1.5px, transparent 1.5px 6px)",
              opacity: 0.15
            }} />}
              </div>

              <div className="p-6 flex flex-col flex-1 gap-4">
                
                <h3 onClick={() => {
              window.location.href = post.link || "#";
            }} className="font-default font-bold text-foreground leading-[1.15] cursor-pointer" style={{
              fontSize: "clamp(1.125rem, 1.4vw, 1.375rem)"
            }}>
                  {post.title}
                </h3>

                {excerpt && <p className="font-serif text-muted-foreground leading-[1.55] flex-1" style={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden"
            }}>
                    {excerpt}
                  </p>}

                <div className="flex justify-end pt-2">
                  <Button onClick={() => {
                window.location.href = post.link || "#";
              }} className="bg-secondary text-secondary-foreground border-[3px] border-border rounded-md px-5 py-2.5 font-default text-xs uppercase tracking-[0.04em] hover:bg-secondary/90 transition-all duration-200">
                    Read More
                    <ArrowRight className="ml-1.5 h-4 w-4" strokeWidth={2.5} />
                  </Button>
                </div>
              </div>
            </article>;
      })}
      </div>

      {totalPages > 1 && <div className="mt-16 flex justify-center">
          <Pagination>
            <PaginationContent className="gap-2 flex-wrap">
              <PaginationItem>
                <Button onClick={prevPage} disabled={!hasPrev} className="bg-card text-foreground border-[3px] border-border rounded-md px-4 py-2 font-mono text-xs uppercase tracking-[0.12em] hover:bg-muted disabled:opacity-40 transition-colors">
                  ← PREV
                </Button>
              </PaginationItem>

              {Array.from({
            length: totalPages
          }, (_, idx) => idx + 1).map((pageNum, i) => <PaginationItem data-index={i} key={pageNum}>
                    <Button onClick={() => setPage(pageNum)} className={pageNum === currentPage ? "bg-primary text-primary-foreground border-[3px] border-border rounded-md w-11 h-11 p-0 font-mono text-sm font-medium" : "bg-card text-foreground border-[3px] border-border rounded-md w-11 h-11 p-0 font-mono text-sm font-medium hover:bg-muted transition-colors"}>
                      {String(pageNum).padStart(2, "0")}
                    </Button>
                  </PaginationItem>)}

              <PaginationItem>
                <Button onClick={nextPage} disabled={!hasNext} className="bg-card text-foreground border-[3px] border-border rounded-md px-4 py-2 font-mono text-xs uppercase tracking-[0.12em] hover:bg-muted disabled:opacity-40 transition-colors">
                  NEXT →
                </Button>
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>}
    </>;
}
export default function Blog() {
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");
  const wp_query_params = {
    post_type: "post",
    per_page: 9,
    order: sortOrder === "oldest" ? "asc" : "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="1645" id="blogarticlelisting" className="bg-background text-foreground relative border-t-[4px] border-b-[4px] border-border pt-10 pb-28 md:pt-14 md:pb-40 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{
      background: "repeating-radial-gradient(circle, var(--border) 0 1.5px, transparent 1.5px 8px)",
      opacity: 0.04
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 mb-16 lg:mb-24">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-6 h-[3px] bg-primary" />
              <span className="font-mono text-xs sm:text-sm uppercase tracking-[0.18em] text-foreground font-medium">
                § 01 — CARDIOLOGY INSIGHTS
              </span>
            </div>
            <h2 className="font-default font-black text-foreground leading-[0.92]" style={{
            fontSize: "clamp(2rem, 4.5vw, 4rem)",
            letterSpacing: "-0.035em"
          }}>
              <span className="block">HEART</span>
              <span className="block">HEALTH</span>
              <span className="inline-flex items-center flex-wrap">
                <span className="relative inline-block">
                  <span className="relative z-10 text-primary">
                    UPDATES.
                  </span>
                </span>
              </span>
            </h2>
            <p className="mt-8 font-serif text-base sm:text-lg text-muted-foreground max-w-2xl leading-[1.55]">
              Expert guidance on cardiovascular wellness, prevention, and the latest in heart care technology.
            </p>
          </div>

          <div className="lg:col-span-4 flex flex-col gap-4">
            <div>
              <label className="block font-mono text-xs uppercase tracking-[0.18em] text-foreground mb-2 font-medium">
                SEARCH:
              </label>
              <Input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="SEARCH ARTICLES..." className="h-14 bg-card border-[3px] border-border rounded-md px-4 font-serif text-base text-foreground placeholder:text-muted-foreground placeholder:uppercase placeholder:tracking-wider focus-visible:ring-0 focus-visible:border-primary" />
            </div>

            <div>
              <label className="block font-mono text-xs uppercase tracking-[0.18em] text-foreground mb-2 font-medium">
                CATEGORY:
              </label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-14 bg-card border-[3px] border-border rounded-md px-4 font-mono text-sm uppercase tracking-[0.12em] text-foreground focus:ring-0 focus:border-primary">
                  <SelectValue placeholder="ALL" />
                </SelectTrigger>
                <SelectContent className="bg-card border-[3px] border-border rounded-md">
                  <SelectItem value="all" className="font-mono text-sm uppercase tracking-[0.12em]">
                    ALL TOPICS
                  </SelectItem>
                  <SelectItem value="prevention" className="font-mono text-sm uppercase tracking-[0.12em]">
                    PREVENTION
                  </SelectItem>
                  <SelectItem value="procedures" className="font-mono text-sm uppercase tracking-[0.12em]">
                    PROCEDURES
                  </SelectItem>
                  <SelectItem value="wellness" className="font-mono text-sm uppercase tracking-[0.12em]">
                    WELLNESS
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block font-mono text-xs uppercase tracking-[0.18em] text-foreground mb-2 font-medium">
                SORT:
              </label>
              <div className="grid grid-cols-2 gap-0 border-[3px] border-border rounded-md overflow-hidden">
                <Button onClick={() => setSortOrder("newest")} className={sortOrder === "newest" ? "bg-primary text-primary-foreground rounded-none h-12 font-mono text-xs uppercase tracking-[0.12em] hover:bg-primary/90 border-r-[3px] border-border" : "bg-card text-foreground rounded-none h-12 font-mono text-xs uppercase tracking-[0.12em] hover:bg-muted border-r-[3px] border-border"}>
                  NEWEST
                </Button>
                <Button onClick={() => setSortOrder("oldest")} className={sortOrder === "oldest" ? "bg-primary text-primary-foreground rounded-none h-12 font-mono text-xs uppercase tracking-[0.12em] hover:bg-primary/90" : "bg-card text-foreground rounded-none h-12 font-mono text-xs uppercase tracking-[0.12em] hover:bg-muted"}>
                  OLDEST
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full h-[2px] mb-12" style={{
        background: "repeating-linear-gradient(to right, var(--border) 0 6px, transparent 6px 12px)"
      }} aria-hidden="true" />

        <PostsProvider wp_query={wp_query}>
          <ArchiveConsumer searchTerm={searchTerm} category={category} sortOrder={sortOrder} />
        </PostsProvider>
      </div>
    </section>;
}